import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";

/**
 * Given a timeRemaining in milliseconds, returns the time remaining in minutes and seconds.
 * Seconds always contain 2 digits.
 */
export const formatTimer = (timeRemaining: number) => {
  // calculate time left
  const hours = Math.floor((timeRemaining / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);

  const hourString = hours.toString();
  const minString = minutes.toString();
  let secString = seconds.toString();

  if (secString.length === 1) {
    secString = `0${seconds}`;
  }

  return { hours: hourString, minutes: minString, seconds: secString };
};

/**
 * Given an arbitrary date in the future, performs a countdown to that date ticking every second.
 * Returns the time remaining in milliseconds
 */
export const useTimer = (
  defaultTargetDate?: UseTimerProps["defaultTargetDate"],
  opts?: UseTimerProps["options"]
): [
  number,
  React.Dispatch<React.SetStateAction<UseTimerProps["defaultTargetDate"]>>
] => {
  const [targetDate, setTargetDate] =
    useState<UseTimerProps["defaultTargetDate"]>(defaultTargetDate);
  const [timeRemaining, setTimeRemaining] = useState<number>(() =>
    new Date().getTime()
  );
  const intervalRef = useRef<ReturnType<typeof setTimeout> | undefined>();

  /**
   * Callback function to be called when the target date is reached.
   * Optionally calls a function passed in the options.
   */
  const handleTargetReached = useCallback(() => {
    if (
      typeof opts?.onTargetReached?.handler === "function" &&
      opts?.onTargetReached?.enabled
    ) {
      opts.onTargetReached.handler();
    }
  }, [opts?.onTargetReached?.handler, opts?.onTargetReached?.enabled]);

  /**
   * Calculate new time remaining based on the target date and the current time
   */
  const tick = useCallback(() => {
    if (targetDate != null) {
      const newTimeRemaining =
        new Date(targetDate).getTime() - new Date().getTime();
      setTimeRemaining(newTimeRemaining);
    }
  }, [targetDate]);

  /**
   * Interval logic to perform the actual ticking.
   * The cleanup function clears the interval when the hook unmounts.
   */
  useEffect(() => {
    // Call tick to prevent the flashing of a different time
    // on load when targetDate is set but the interval hasn't started yet.
    tick();

    intervalRef.current = setInterval(() => {
      tick();
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [tick]);

  // useEffect(() => {
  //   setTargetDate(defaultTargetDate);
  // }, [defaultTargetDate]);

  /**
   * When the time remaining reaches zero, clear the interval and
   * perform our target reached logic.
   */
  useEffect(() => {
    if (timeRemaining <= 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      handleTargetReached();
    }
  }, [timeRemaining]);

  return [timeRemaining, setTargetDate];
};

type UseTimerProps = {
  defaultTargetDate: string | Date | undefined | null;
  options?: {
    onTargetReached?: {
      /**
       * Callback function to be called when the target date is reached.
       */
      handler: () => void;
      enabled: boolean;
    };
  };
};

const TimerContext = createContext<null | {
  timeRemaining: number;
  setTimeRemaining: React.Dispatch<
    React.SetStateAction<UseTimerProps["defaultTargetDate"]>
  >;
}>(null);

/**
 * Context provider for syncing timers across an entire subtree.
 */
export const TimerProvider = ({
  children,
}: {
  children: JSX.Element;
}): JSX.Element => {
  const [timeRemaining, setTimeRemaining] = useTimer();

  return (
    <TimerContext.Provider value={{ timeRemaining, setTimeRemaining }}>
      {children}
    </TimerContext.Provider>
  );
};

/**
 * Given an arbitrary date in the future, performs a countdown to that date ticking every second.
 * This hook allows for syncing useTimers across an entire subtree.
 */
export const useTimerContext = (targetDate: string | Date) => {
  const context = useContext(TimerContext);

  if (!context) {
    throw new Error(
      `useTimerContext must be used within a TimerProvider to sync timers. 
      If you do not need to sync timers, consider using useTimer directly.`
    );
  }

  useEffect(() => {
    context.setTimeRemaining(targetDate);
  }, [targetDate]);

  return context;
};
